import clsx from 'clsx';
import React from 'react';

import Byline from '@components/Byline';
import Highlight from '@components/FeedItems/Highlight';
import IconProTagThumbnail from '@components/Images/Rebranding/IconProTagThumbnail';
import LazyImgixImage from '@components/LazyImgixImage';
import { useAppContext } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import { briefing_path } from '@routes';
import { ClickTarget, ContentType, click } from '@tracking';
import { BookmarkableType } from '@utils/backend-api/bookmarks';
import { getArticleImgSrc } from '@utils/images';
import { articlePath } from '@utils/queryStringHelper';

import type { FeaturedContentProps } from './interfaces';

const FeaturedContent = ({ className, featuredContent, isFluid = false }: FeaturedContentProps) => {
  const { env } = useAppContext();
  const {
    authors,
    commentsCount,
    highlight,
    id,
    isPro,
    picture,
    pictureCaption,
    publishedAt,
    slug,
    title,
    newsletterSlug,
  } = featuredContent;
  const { trackEvent, Track } = useEventTracking({
    contentType:
      featuredContent.type === BookmarkableType.Article
        ? ContentType.Article
        : ContentType.Briefing,
    contentId: id,
    creatorIds: authors?.map((author) => author.id) || [],
  });

  const targetLink =
    featuredContent.type === BookmarkableType.Article
      ? articlePath({ slug, newsletterSlug })
      : briefing_path(slug);

  return (
    <Track>
      <article
        className={clsx(
          'flex-1 border-0 border-b border-solid border-grayscale-gray px-16 pb-32 sm:px-0',
          className
        )}
      >
        <a
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.ArticleImage }))}
          href={targetLink}
          className={clsx('relative -mx-16 mb-16 block sm:mx-0 sm:mb-24', 'first-of-type:!mx-0', {
            'h-[calc((100vw)*9/16)] sm:h-[calc((100vw-3.5rem)*9/16)] md:h-[calc(712px*9/16)] md:min-w-[712px] lg:h-[calc(884px*9/16)] lg:min-w-[884px]':
              !isFluid,
            'aspect-[16/9] flex-shrink flex-grow text-[1px] leading-none sm:w-full lg:max-w-[884px]':
              isFluid,
          })}
        >
          {isPro && <IconProTagThumbnail className="absolute right-0 top-0" />}
          <LazyImgixImage
            alt={pictureCaption || title}
            className="h-auto w-full"
            src={
              featuredContent.type === BookmarkableType.Article
                ? getArticleImgSrc(id, picture, env)
                : picture
            }
            sizes="(min-width: 480px) 600px, 400px"
            wrapperClassName="h-full w-full"
            visibleByDefault
          />
        </a>
        {highlight && (
          <Highlight className="text-overline-sm sm:text-overline-md" highlight={highlight} />
        )}
        <a
          onClickCapture={() =>
            trackEvent(click({ clickTarget: ClickTarget.Headline, clickText: title }))
          }
          href={targetLink}
          className="text-balance"
        >
          <h2 className="m-0 text-balance font-suisse-intl text-body-md font-normal text-primary-black hover:text-primary-magenta sm:font-suisse-works sm:text-headline-xxs dark:hover:text-primary-pink">
            {title}
          </h2>
        </a>
        <div>
          <Byline
            authors={authors}
            authorsClassName="hidden sm:inline"
            bookmarkContentId={id}
            bookmarkContentType={
              featuredContent.type === BookmarkableType.Article
                ? ContentType.Article
                : ContentType.Briefing
            }
            newsletterSlug={newsletterSlug}
            commentsCount={commentsCount}
            publishedAt={publishedAt}
            slug={slug}
          />
        </div>
      </article>
    </Track>
  );
};

export default FeaturedContent;
